import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserPackageAssignments, fetchUserPackageAssignmentsWithLicenseInfo } from 'common/services/api/ProductPackages/PackageAssignments.thunk';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { GetPackageAssignmentDrawerContent } from 'features/productPackageAssignment/ProductPackageAssignmentDrawerContent';
import { PackageAssignmentPage } from 'features/productPackageAssignment/productPackageAssignmentTypeDefs';
import { enqueueSnackbar } from 'notistack';

import { PalmDataGrid } from '../../common/components/PalmDataGrid';

import { getActionDefinitions, getColumnDefinitions } from './helpers';
import { GetUserProductPackageDeleteDialogContent } from './userProductPackageDeleteDialogContent';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilters } from './userProductPackagesSlice';

export function UserProductPackagesTab() {

    const typeName = 'Product Packages';
    const [processingDefault, setProcessingDefault] = useState<boolean>(false);
    const [processingLicense, setProcessingLicense] = useState<boolean>(false);
    const [sortModel, setSortModel] = usePalmSortModel('packageName');
    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters);

    const { tenantId = '', userId = '', suiteId = '' } = useParams();
    const pageInfo = useAppSelector(selectPageInfo);
    const dispatch = useAppDispatch();


    const fetchDefault = useCallback(() => {
        setProcessingDefault(true);
        dispatch(fetchUserPackageAssignments({ tenantId, userId }))
            .unwrap()
            .then((rows) => dispatch(setRows(rows)))
            .catch((error) => {
                dispatch(setRows([]));
                handleListFetchError(extractStatusCode(error.message), typeName, enqueueSnackbar);
            })
            .finally(() => setProcessingDefault(false));
    }, [tenantId, userId]);
    
    const fetchWithLicense = useCallback(() => {
        setProcessingLicense(true);
        dispatch(fetchUserPackageAssignmentsWithLicenseInfo({ tenantId, userId }))
            .unwrap()
            .then((rows) => dispatch(setRows(rows)))
            .catch((error) => {
                dispatch(setRows([]));
                handleListFetchError(extractStatusCode(error.message), typeName, enqueueSnackbar);
            })
            .finally(() => setProcessingLicense(false));
    }, [tenantId, userId]);
    
    useEffect(() => {
        if (pageInfo?.tenantRequiresLicense) {
            fetchWithLicense();
        } else if (pageInfo?.tenantRequiresLicense === false) {
            fetchDefault();
        }
    }, [pageInfo?.tenantRequiresLicense, fetchWithLicense, fetchDefault]);
    
    const refreshDataGrid = pageInfo?.tenantRequiresLicense ? fetchWithLicense : fetchDefault;

    const getProductPackageUrlCallBack = useCallback((id: string) => {
        const productPackageId = id.split('.')[1];
        return `/application-suites/${suiteId}/tenants/${tenantId}/packages/${productPackageId}`;
    }, []);

    const getLicenseUrlCallBack = useCallback((id: string, row?: GridValidRowModel | undefined): string => {
        return `/application-suites/${suiteId}/tenants/${tenantId}/licenses/${row?.licenseId}/packages`;
    }, []);

    const shouldDisableLicenseButtonCallback = useCallback((row?: GridValidRowModel | undefined): boolean => {
        return !pageInfo?.tenantRequiresLicense || row?.licenseId === undefined;
    }, []);


    function GetProductPackageDeleteDialogContent() {
        return GetUserProductPackageDeleteDialogContent({
            tenantId: tenantId,
            tenantName: pageInfo?.name ?? '',
            tenantRequiresLicense: pageInfo?.tenantRequiresLicense ?? false
        });
    }

    const userFullName = [pageInfo?.firstName, pageInfo?.lastName].filter(Boolean).join(' ');

    function GetPPADrawerContent() {
        return GetPackageAssignmentDrawerContent({
            tenantId,
            tenantName: pageInfo?.name ?? '',
            tenantRequiresLicense: pageInfo?.tenantRequiresLicense ?? false,
            page: PackageAssignmentPage.User,
            principalId: userId,
            principalName: userFullName,
        });
    }

    return <PalmDataGrid
        title={typeName}
        processing={pageInfo?.tenantRequiresLicense ? processingLicense : processingDefault}
        actionDefinitions={getActionDefinitions(
            getProductPackageUrlCallBack,
            shouldDisableLicenseButtonCallback,
            getLicenseUrlCallBack,
            shouldDisableButtons,
            pageInfo?.tenantRequiresLicense ?? false
        )}
        columnDefinitions={getColumnDefinitions(pageInfo?.tenantRequiresLicense ?? false)}
        contentFunction={GetPPADrawerContent()}
        isCreateButtonDisabled={shouldDisableButtons}
        customCreateButtonName="Assign Product Package"
        filter={filterProp}
        refreshDataGridClick={refreshDataGrid}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
        getRowLink={getProductPackageUrlCallBack}
        deleteFunction={GetProductPackageDeleteDialogContent()}
    />;
}
